import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import MapEmbed from "@/components/MapEmbed";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { BookButton, SectionHeading } from "@/components/ui-bits";
import NotFound from "@/pages/NotFound";
import { LOCAL_AREAS, HOTEL, IMAGES, AMENITIES } from "@/lib/site";

export function NeighborhoodIndex() {
  return (
    <>
      <Seo
        title="Areas We Serve Near Pittsburg, KS | Regency Inn & Suites"
        description="Regency Inn & Suites serves guests across southeast Kansas — near Pittsburg State University, downtown Pittsburg, the hospital, Frontenac, Joplin MO and Fort Scott."
        path="/near"
        image={IMAGES.lobby}
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Areas We Serve", path: "/near" }])]}
      />
      <PageHero
        eyebrow="Local Landing"
        title="Close to everywhere you need to be."
        subtitle="Just off US Highway 69, we're a convenient, affordable base for the whole region. Find your destination below."
        image={IMAGES.family}
        crumbs={[{ name: "Home", path: "/" }, { name: "Areas We Serve", path: "/near" }]}
      />
      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LOCAL_AREAS.map((a) => (
              <StaggerItem key={a.slug}>
                <Link to={`/near/${a.slug}`} data-testid={`near-${a.slug}`} className="group block border border-stone/15 rounded-md p-7 hover:border-amber transition-colors h-full">
                  <span className="text-xs uppercase tracking-widest text-amber-deep">{a.kind} · {a.dist}</span>
                  <h2 className="font-serif text-2xl text-forest mt-2 group-hover:text-amber-deep transition-colors">{a.name}</h2>
                  <p className="text-stone mt-2 text-[15px] leading-relaxed">{a.intro}</p>
                  <span className="mt-4 inline-block text-sm text-forest link-underline">View details →</span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}

export default function Neighborhood({ slug }) {
  const a = LOCAL_AREAS.find((x) => x.slug === slug);
  if (!a) return <NotFound />;
  return (
    <>
      <Seo
        title={a.seoTitle}
        description={a.seoDescription}
        path={`/near/${a.slug}`}
        image={IMAGES.heroFacade}
        schema={[breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Areas We Serve", path: "/near" },
          { name: a.name, path: `/near/${a.slug}` },
        ])]}
      />
      <PageHero
        eyebrow={`${a.kind} · ${a.dist} away`}
        title={a.title}
        subtitle={a.intro}
        image={IMAGES.roomBoutique}
        crumbs={[{ name: "Home", path: "/" }, { name: "Areas We Serve", path: "/near" }, { name: a.name, path: `/near/${a.slug}` }]}
      />
      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <SectionHeading eyebrow="Why Stay With Us" title={`Your base near ${a.name}.`} />
            <ul className="mt-8 space-y-4">
              {a.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-stone">
                  <Icons.Check size={18} className="text-amber shrink-0 mt-1" /> {h}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <BookButton label="Book Direct & Save" testid="near-book" />
              <a href={HOTEL.phoneHref} data-testid="near-call" className="inline-flex items-center rounded-full border border-forest text-forest px-8 py-4 text-sm font-medium hover:bg-forest hover:text-bone transition-colors">
                Call {HOTEL.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <MapEmbed height="440px" />
          </Reveal>
        </div>
      </section>
      <section className="bg-sand py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {AMENITIES.slice(0, 8).map((am) => {
              const Icon = Icons[am.icon] || Icons.Check;
              return (
                <StaggerItem key={am.title}>
                  <Icon className="text-amber" size={24} strokeWidth={1.4} />
                  <h3 className="font-serif text-xl text-forest mt-3">{am.title}</h3>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>
    </>
  );
}
